import { Fragment, h } from 'preact'; /** @jsx h */
import { useContext, useEffect, useState } from 'preact/hooks';
import { NotificationContext } from '../../context/NotificationContext';

import { getKampValue } from '../../../global-scripts/campaign-tracking';

import Checkout from '../Checkout/Checkout';
import DeliveryInfo from '../DeliveryInfo/DeliveryInfo';
import EmptyCart from '../EmptyCart/EmptyCart';
import Login from '../Login/Login';
import PaymentInfo from '../PaymentInfo/PaymentInfo';
import UserInfo from '../UserInfo/UserInfo';

import NotificationBanner from '../ProductHero/NotificationBanner/NotificationBanner';
import './UserPanel.scss';

const UserPanel = ({
    cart,
    total,
    isDetails,
    registrationDetails,
    setRegistrationDetails,
    setTabletView,
    isTablet,
    errorNumber
}) => {
    const { showNotification } = useContext(NotificationContext);

    // User state
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [isRegistered, setIsRegistered] = useState(false);
    const [user, setUser] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState('vipps');
    const [emailError, setEmailError] = useState(false);

    // Check if user is logged in or registered on page load (from localStorage or window.NLA)
    useEffect(() => {
        const userFromStorage = JSON.parse(localStorage.getItem('snla-user'));

        if (userFromStorage) {
            setUser(userFromStorage);
            setIsLoggedIn(false);
            setIsRegistered(true);
        } else if (window.NLA?.user) {
            const userFromNLA = window.NLA.user;
            localStorage.setItem('snla-user', JSON.stringify(userFromNLA));
            setUser(userFromNLA);
            setIsLoggedIn(true);
            setIsRegistered(false);
        } else {
            setUser(null);
            setIsLoggedIn(false);
            setIsRegistered(false);
        }
    }, []);

    // Handle checkout form submit programmatically (to be able to add hidden input fields)
    const handleCheckout = e => {
        // Prevent default form submit
        e.preventDefault();

        // Check if user is missing, email is missing, cart is missing or payment method is missing
        if (!user) {
            showNotification('error', 'Bruker mangler. Vennligst logg inn.', '', '', 10);
            return;
        } else if (!user.ho_epost) {
            setEmailError(true);
            showNotification(
                'error',
                'E-post mangler. Vennligst skriv inn din e-post i feltet under punkt 1. Leveringsopplysninger.',
                '',
                '',
                10
            );
            return;
        } else if (!cart) {
            showNotification(
                'error',
                'Handlekurv mangler. Vennligst legg til varer i handlekurven.',
                '',
                '',
                10
            );
            return;
        } else if (!paymentMethod) {
            showNotification(
                'error',
                'Betalingsmetode mangler. Vennligst velg en betalingsmetode.',
                '',
                '',
                10
            );
            return;
        } else if (errorNumber === 99) {
            showNotification(
                'error',
                'Det har oppstått en feil. Vennligst ta kontakt med oss for å fortsette.',
                '',
                '',
                10
            );
            return;
        }

        // Get kamp value from localStorage
        const kampValue = getKampValue();

        // Create order object
        const order = {
            kamp: kampValue || '',
            formUrl: window.location.pathname,
            betalingsform: paymentMethod,
            ho_mobilnr: user.ho_mobilnr,
            ho_fnavn: user.ho_fnavn,
            ho_enavn: user.ho_enavn,
            adresse1: user.adresse1,
            postnr: user.postnr,
            poststed: user.poststed,
            ho_epost: user.ho_epost,
            epost_samt: user.ho_epost_samt,
            products: cart.map(product => {
                const sendmail = product.shipmentType === 'Gavebevis på e-post';
                const sendpost = product.shipmentType === 'Gavebevis i posten';

                return {
                    id: product.id,
                    antall: product.qty,
                    sum: product.inputPrice || product.donation || product.price,
                    gb_sendmail: sendmail,
                    gb_sendpost: sendpost
                };
            })
        };

        // Create hidden input fields for each field in the order object
        for (const [key, value] of Object.entries(order)) {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = key;
            input.value = typeof value === 'object' ? JSON.stringify(value) : value;
            document.getElementById('checkoutForm').appendChild(input);
        }

        // Now submit the form programmatically
        e.target.form.submit();
    };

    return (
        <div className="ws-order-container">
            {isDetails ? (
                <>
                    {/* UserPanel - Order details */}
                    {isLoggedIn || isRegistered ? (
                        <>
                            <UserInfo user={user} setUser={setUser} emailError={emailError} />
                            <DeliveryInfo cart={cart} />
                            <PaymentInfo
                                cart={cart}
                                paymentMethod={paymentMethod}
                                setPaymentMethod={setPaymentMethod}
                            />
                            <Checkout
                                user={user}
                                isTablet={isTablet}
                                setTabletView={setTabletView}
                                total={total}
                                handleCheckout={handleCheckout}
                            />
                        </>
                    ) : (
                        <Login
                            isTablet={isTablet}
                            setTabletView={setTabletView}
                            setUser={setUser}
                            setIsRegistered={setIsRegistered}
                            registrationDetails={registrationDetails}
                            setRegistrationDetails={setRegistrationDetails}
                        />
                    )}
                </>
            ) : (
                <EmptyCart />
            )}
            <NotificationBanner />
        </div>
    );
};

export default UserPanel;
